/* eslint-disable react-hooks/exhaustive-deps */
import adapt, { presenceService } from "@turnaroundfactor/common";
import axios from "axios";
import * as sdk from "microsoft-cognitiveservices-speech-sdk";
import React, { useEffect, useState, useRef } from "react";
import { languages } from "./languageslist";
import { useAudioQueue } from "./useAudioQueue";
import { toast } from "react-toastify";
import Toast from "../../components/Toast";
import { useSettings } from "@turnaroundfactor/react";
import { useTranslationStore } from "../../hooks/useTranslation";
import { useShallow } from "zustand/react/shallow";
import { v4 } from "uuid";

export default function TranslatorCompanion() {
	const [recognizers, setRecognizers] = useState({});
	const [peers, setPeers] = useState([]);
	const { enqueueAudio, clearQueue, audioRef } = useAudioQueue();

	const translation = useRef(null);

	const {
		settings: { translationEnabled, translationAudioEnabled, language, voice },
	} = useSettings();

	const mutedRef = useRef(!translationAudioEnabled);

	const translatorRunning = translationEnabled;
	const { addMessage, updateMessage } = useTranslationStore(
		useShallow((store) => ({
			addMessage: store.addMessage,
			updateMessage: store.updateMessage,
		}))
	);

	useEffect(() => {
		const connections = adapt.room.connections;
		setPeers(connections);

		adapt.room.on("USER_CONNECT", (user) => {
			const connections = adapt.room.connections;

			setPeers(connections);
			connections.forEach((peer) => {
				if (peer.uuid === user.uuid) {
					peer.on("CONNECTED", () => {
						if (translatorRunning) {
							if (peer.type === "Tool") return;
							addPeerRecognizer(peer, language, voice);
						}
					});
				}
			});
		});

		adapt.room.on("DISCONNECTED", () => {
			if (translatorRunning || Object.keys(recognizers).length) {
				for (const id in recognizers) {
					removePeerRecognizer(id);
				}
			}
		});

		return () => {
			if (translatorRunning || Object.keys(recognizers).length) {
				stopTranslation();
			}
		};
	}, []);

	useEffect(() => {
		console.log("[Translation] Translation enabled: ", translationEnabled);
		if (translationEnabled === true) handleStartTranslations();
		else stopTranslation();
	}, [translationEnabled]);

	useEffect(() => {
		mutedRef.current = !translationAudioEnabled;
		if (audioRef.current) {
			audioRef.current.muted = !translationAudioEnabled;
		}
	}, [translationAudioEnabled]);

	async function addPeerRecognizer(peer, voice) {
		if (recognizers[peer.uuid]) return;
		if (!peer.remoteTracks) return;

		const mediaStream = new MediaStream(
			peer.remoteTracks.map((t) => t.clone())
		);
		const targetLanguage = languages.find((lang) => lang.code === language);

		try {
			const { data } = await axios.get(presenceService("translator"));

			const { subscription, region } = data;

			const speechConfig = sdk.SpeechTranslationConfig.fromSubscription(
				subscription,
				region
			);

			console.log("speechConfig", typeof speechConfig);

			const translationCode = targetLanguage.code;

			let recognizer;

			speechConfig.addTargetLanguage(translationCode);
			speechConfig.speechRecognitionLanguage = targetLanguage.extendedCode;
			speechConfig.setProperty(
				sdk.PropertyId.SpeechServiceConnection_TranslationVoice,
				targetLanguage.voice[voice]
			);
			speechConfig.setProperty(
				sdk.PropertyId
					.SpeechServiceResponse_TranslationRequestStablePartialResult,
				"true"
			);
			speechConfig.setProperty(
				sdk.PropertyId.Speech_SegmentationStrategy,
				"Semantic"
			);
			speechConfig.setProperty(
				sdk.PropertyId.Speech_StartEventSensitivity,
				"high"
			);

			const audioConfig = sdk.AudioConfig.fromStreamInput(mediaStream);
			speechConfig.setProperty(
				sdk.PropertyId.SpeechServiceConnection_RecoMode,
				"Interactive"
			);
			const autoDetectSpeechLanguageConfig =
				sdk.AutoDetectSourceLanguageConfig.fromOpenRange();
			recognizer = sdk.TranslationRecognizer.FromConfig(
				speechConfig,
				autoDetectSpeechLanguageConfig,
				audioConfig
			);
			console.log("recognizer:", recognizer);

			recognizer.sessionStarted = (s, e) => {
				console.log("[Translation] Session started for:", peer.uuid);
			};

			recognizer.recognizing = (s, e) => {
				const translatedText = e.result.translations.get(translationCode);
				console.log("RECOGNIZER.RECOGNIZING", translatedText);

				if (!translatedText) return;

				const last = translation.current;

				if (last) {
					updateMessage(last, {
						body: translatedText,
					});

					return;
				}

				const msgId = v4();
				translation.current = msgId;

				addMessage({
					uuid: msgId,
					user: { uuid: peer.uuid, name: peer.name },
					partial: true,
					body: translatedText,
					datetime: new Date(),
					language: translationCode,
				});
			};

			recognizer.recognized = (s, e) => {
				if (e.result.reason !== sdk.ResultReason.TranslatedSpeech) return;
				const translatedText = e.result.translations.get(translationCode);

				console.log("RECOGNIZER.RECOGNIZED", translatedText);

				if (!translatedText.length) return;

				const last = translation.current;

				if (last) {
					updateMessage(last, {
						body: translatedText,
						partial: false,
					});
				} else {
					addMessage({
						uuid: v4(),
						user: { uuid: peer.uuid, name: peer.name },
						partial: false,
						body: translatedText,
						datetime: new Date(),
						language: translationCode,
					});
				}

				translation.current = null;
			};

			recognizer.synthesizing = (s, e) => {
				if (!e.result.audio) return;

				console.log("SYNTHESIZED", e.result);
				console.log("muteRef", mutedRef.current);

				if (mutedRef.current) {
					clearQueue();
					return;
				}
				const blob = new Blob([e.result.audio], { type: "audio/wav" });
				enqueueAudio({ peerId: peer.uuid, blob: blob });
			};

			recognizer.sessionStopped = (s, e) => {
				console.log(
					"[Translation] Session successfully stopped for user:",
					peer.uuid
				);
			};

			recognizers[peer.uuid] = recognizer;
			setRecognizers(recognizers);

			peer.on("DISCONNECT", () => {
				console.log("[Translation] DISCONNECT - STOPPING RECOGNIZER");
				removePeerRecognizer(peer.uuid);
			});

			console.log(
				`[Translation] Recognizer made for user ${peer.uuid}`,
				recognizer
			);

			recognizer.startContinuousRecognitionAsync();
			return true;
		} catch (e) {
			console.log("Unable to process translation:", e);
			return false;
		}
	}

	function removePeerRecognizer(uuid) {
		const recognizer = recognizers[uuid];
		if (recognizer) {
			recognizer.stopContinuousRecognitionAsync(
				() => {
					console.log(
						`[Translation] stopped recognition for user: ${uuid}`,
						recognizer
					);
					recognizer.close();
					delete recognizers[uuid];
					setRecognizers(recognizers);
				},
				() => recognizer.close()
			);
		}
	}

	async function handleStartTranslations() {
		if (peers.length === 0) {
			const icon = "fa-exclamation-triangle";
			toast(
				<Toast
					title={false}
					icon={icon}
					message={`Must have other users in the room to start translating.`}
				/>,
				{
					position: "top-right",
					autoClose: 3000,
					closeOnClick: true,
					hideProgressBar: false,
					draggable: false,
					theme: "colored",
				}
			);
			return;
		}
		let peerRecognizers;

		try {
			console.log("peers", peers);
			peerRecognizers = await Promise.allSettled(
				peers.map(async (peer) => {
					if (peer.type === "Tool") {
						return true;
					}

					const recognizerMade = await addPeerRecognizer(peer, voice);
					if (!recognizerMade) {
						return false;
					} else {
						return true;
					}
				})
			);

			const hasFalse = peerRecognizers.some(
				(r) => r.status === "fulfilled" && r.value === false
			);

			if (hasFalse) throw new Error("Unable to process translations.");
		} catch (e) {
			console.log("Error with running translator:", e);
			stopTranslation();

			const icon = "fa-exclamation-triangle";

			toast(
				<Toast
					title={false}
					icon={icon}
					message={`Unable to start translation service. Please try again.`}
				/>,
				{
					position: "top-right",
					autoClose: 3000,
					closeOnClick: true,
					hideProgressBar: false,
					draggable: false,
					theme: "colored",
				}
			);
		}
	}

	function stopTranslation() {
		for (const id in recognizers) {
			removePeerRecognizer(id);
		}

		clearQueue();
	}

	return <audio id="translatedAudio" ref={audioRef}></audio>;
}
